import { useState, useEffect, useCallback } from 'react'
import { CheckSquare, AlertCircle } from 'lucide-react'
import { TaskCard, Pagination, Button } from '@anyemp/ui-kit'
import { tasks as tasksService } from '../../services/apiService'
import { Loader } from '../UI/LoadingSpinner'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'

const DEFAULT_LIMIT = 5

const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit' })

const TasksSection = ({ authUserId }) => {
  const [tasks, setTasks] = useState([])
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT, totalPages: 0 })
  const [loading, setLoading] = useState(!!authUserId)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [statusesById, setStatusesById] = useState({})

  useEffect(() => {
    let cancelled = false
    tasksService.listStatuses()
      .then((res) => {
        if (cancelled) return
        const list = res?.data || res || []
        const map = {}
        ;(Array.isArray(list) ? list : []).forEach((s) => { map[s.id] = s })
        setStatusesById(map)
      })
      .catch(() => { /* permission likely missing — silent fallback: no status pill */ })
    return () => { cancelled = true }
  }, [])

  const fetchTasks = useCallback(async () => {
    if (!authUserId) return
    try {
      setLoading(true)
      setError(null)
      const res = await tasksService.list({ assignee_id: authUserId, page, limit: DEFAULT_LIMIT })
      setTasks(res?.data || [])
      setPagination(res?.pagination || { total: 0, page: 1, limit: DEFAULT_LIMIT, totalPages: 0 })
    } catch (err) {
      console.error('Error fetching tasks:', err)
      setError(err?.response?.data?.message || err.message || 'Error loading tasks')
    } finally {
      setLoading(false)
    }
  }, [authUserId, page])

  useEffect(() => {
    if (authUserId) fetchTasks()
  }, [authUserId, page, fetchTasks])

  const openTask = (task) => {
    if (task.web_url) window.open(task.web_url, '_blank', 'noopener,noreferrer')
  }

  return (
    <SectionBlock id="tasks">
      <SectionHeader icon={CheckSquare} title="Tasks" />

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader variant="component" size="md" fullHeight={false} />
        </div>
      ) : !authUserId ? (
        <div className="text-center py-10">
          <CheckSquare className="mx-auto h-12 w-12 text-light-text-tertiary dark:text-dark-text-tertiary mb-3" />
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-1">User not assigned</p>
          <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">Assign a user to this talent to view tasks</p>
        </div>
      ) : error ? (
        <div className="text-center py-8">
          <AlertCircle className="mx-auto h-10 w-10 text-red-400 dark:text-red-500 mb-3" />
          <p className="text-sm text-red-500 dark:text-red-400 mb-3">{error}</p>
          <Button size="small" variant="text" color="primary" onClick={() => { setError(null); fetchTasks() }}>
            Try again
          </Button>
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8">
          <CheckSquare className="mx-auto h-10 w-10 text-light-text-tertiary dark:text-dark-text-tertiary mb-3" />
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">No assigned tasks</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
            Total: {pagination.total ?? tasks.length}
          </div>
          <div className="space-y-2">
            {tasks.map((task) => {
              const s = statusesById[task.status_id]
              const statusInfo = s ? { label: s.label || s.name, color: s.color } : undefined
              return (
                <TaskCard
                  key={task.id}
                  task={task}
                  onClick={() => openTask(task)}
                  formatDate={formatDate}
                  statusInfo={statusInfo}
                />
              )
            })}
          </div>
          {pagination.totalPages > 1 && (
            <Pagination
              currentPage={pagination.page || page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit || DEFAULT_LIMIT}
              onPageChange={setPage}
              showResults="range"
            />
          )}
        </div>
      )}
    </SectionBlock>
  )
}

export default TasksSection
